import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import _find from 'lodash/find';
import size from 'lodash/size';
import { PLATFORMS } from '@sonifi/wb-screenbuilder/constants/wbScreenBuilderConstants';
import {
  fillerMock,
  GRID_PAGE_SIZE,
  HORIZONTAL_PAGE_SIZE,
  MAX_CHILDREN,
  MAX_DEPTH,
  MENU_STYLE,
} from '../constants/wbHDContansts';
import { buildPlainNodesfromMenu } from '@sonifi/wb-tree/utils/helper';

const intersection = (arr, ...args) =>
  arr.filter((item) => args.every((arr) => arr.includes(item)));

const doesScreenHaveUnpublishedChanges = (node, lastPublished) => {
  const screenDate = node.screen?.last_updated
    ? Math.max(new Date(node.screen.last_updated), node.embeddedResourceLastUpdate || 0)
    : null;
  const lastP = lastPublished
    ? typeof lastPublished === 'string'
      ? new Date(lastPublished)
      : lastPublished
    : null;
  return node.screen.isZombie || screenDate > lastP;
};

export const hasDeletedContent = ({ screen, mediaDependencies }) => {
  let resp = false;

  screen.regions.forEach((r) => {
    if (r.type === 'service_link') {
      const videoUri = r.content.service_link.target;
      resp = videoUri
        ? !mediaDependencies.some((md) => md.uri === videoUri)
        : false;
    } else if (r.type === 'image') {
      const imgUri = r.content[0]?.image || false;
      resp = imgUri
        ? !mediaDependencies.some((md) => md.uri === imgUri)
        : false;
    }
  });
  return resp;
};

const doesNodeHaveChildrenWithUnpublishedChanges = (node, lastPublished) => {
  let hasError = false;
  const plainMenuNodes = buildPlainNodesfromMenu({
    menu: node,
    init: true,
  });
  for (const ch of plainMenuNodes) {
    if (doesScreenHaveUnpublishedChanges(ch, lastPublished)) {
      hasError = true;
    }
  }
  return hasError;
};

export const getHasUnpublishedChanges = ({
  compendiaMenu,
  lastPublished,
  productSettings,
  background,
}) =>
  new Date(compendiaMenu.last_updated) > lastPublished ||
  new Date(productSettings.last_updated) > lastPublished ||
  new Date(background?.last_updated) > lastPublished ||
  doesNodeHaveChildrenWithUnpublishedChanges(compendiaMenu.menu, lastPublished);

export const validate = ({ node, lastPublished }) => {
  if (node.hasDeletedContent) {
    return 'References deleted content';
  } else if (doesScreenHaveUnpublishedChanges(node, lastPublished)) {
    return 'Has unpublished changes';
  }
};

export const getTypes = (node) => {
  const regionTypes = node.screen.regions.map((r) => r.type);
  const types = intersection(['menu', 'service_link'], regionTypes);

  if (!size(types)) {
    types.push('content');
  } else if (types.includes('service_link')) {
    types.push(
      ...node.screen.regions
        .filter((r) => r.content.service_link?.type)
        .map((r) => r.content.service_link.type),
    );
  }
  return types.join(' ');
};

export const getPublishStatusMessage = ({
  lastPublishJob,
  isIncompleteJob,
  isFailedJob,
}) => {
  if (isFailedJob) {
    return 'Failed to publish on:';
  }
  if (isIncompleteJob) {
    return 'Publish request submitted on:';
  }
  if (!lastPublishJob) {
    return 'No published directory';
  }
  return 'Last Published:';
};

const isBeyondMaxDepth = (depth) => depth >= MAX_DEPTH;

const isBeyondMaxChildren = (childrenCount) => childrenCount >= MAX_CHILDREN;

export const getAddTooltip = (node, depth) => {
  if (isBeyondMaxDepth(depth)) {
    return {
      title:
        'You cannot add items to this menu item as it is beyond the maximum depth of this tree',
      disabled: true,
    };
  }

  if (isBeyondMaxChildren(node.children?.length || 0)) {
    return {
      title: `You cannot add more than ${MAX_CHILDREN} items to this tree`,
      disabled: true,
    };
  }

  return {
    title: 'Add',
    disabled: false,
  };
};

export const updateScreenType = (regions) => {
  const regionTypes = regions.map((r) => r.type);
  const typeTmp = intersection(['menu', 'service_link'], regionTypes);
  let type = typeTmp.length ? typeTmp[0] : 'content';

  if (type === 'service_link') {
    type = get(
      _find(regions, { type: 'service_link' }),
      'content.service_link.type',
      'video',
    );
  }

  return type;
};

const exposedTvRegionTypes = ({ type, menuStyle }) => {
  switch (type) {
    case 'menu':
      return menuStyle === MENU_STYLE.vertical
        ? ['menu', 'image', 'text', 'qr']
        : menuStyle === MENU_STYLE.simpleGrid
          ? ['menu', 'image', 'qr']
          : ['menu'];
    case 'video':
      return ['service_link', 'qr'];
    case 'service':
      return ['service_link'];
    default:
      return ['text', 'image', 'qr'];
  }
};

const exposedPhoneRegionTypes = (type) => {
  switch (type) {
    case 'menu':
      return ['menu'];
    case 'service':
    case 'video':
      return ['menu_link', 'service_link'];
    default:
      return ['menu_link', 'text', 'image'];
  }
};

export const filterModelRegions = ({ menuStyle, screen, platform, type }) => {
  const regions = [...screen.regions];
  const regionsTypes =
    platform === PLATFORMS.tv
      ? exposedTvRegionTypes({
          type,
          menuStyle,
        })
      : exposedPhoneRegionTypes(type);
  // if we aren't supposed to filter, just return the entire regions array
  if (!Array.isArray(regionsTypes)) {
    return regions;
  }

  const regionsByType = groupBy(regions, 'type');

  return regionsTypes
    .reduce((acc, type) => {
      if (type === 'qr' && regionsByType.qr) {
        acc.push(...regionsByType.qr);
      } else if (regionsByType[type]?.[0]) {
        acc.push(regionsByType[type][0]);
      }
      return acc;
    }, []);
};

export const isBranch = (node) => {
  const screen = node.screen;
  return (
    (screen.isZombie && !!node.children.length) ||
    screen.regions.some((r) => r.type === 'menu')
  );
};

export const hiddenSectionsByMenuStyle = ({
  menuStyle,
  hiddenSections = [],
}) => {
  switch (menuStyle) {
    case MENU_STYLE.horizontal:
      hiddenSections.push(MENU_STYLE.grid);
      break;
    case MENU_STYLE.vertical:
      hiddenSections.push(...[PLATFORMS.tv, MENU_STYLE.grid]);
      break;
    case MENU_STYLE.grid:
    case MENU_STYLE.simpleGrid:
      hiddenSections.push(PLATFORMS.tv);
      break;
    default:
      break;
  }
  return hiddenSections;
};

export const calculateNumPages = ({ scrollWidth, pageWidth }) =>
  Math.ceil(scrollWidth / pageWidth);

export const getNodeChildrenActive = (nodePreviewSelected) =>
  nodePreviewSelected.children?.filter((ch) => ch.active) || [];

export const configureHorizontalMenu = (nodePreviewSelected) => {
  let children = getNodeChildrenActive(nodePreviewSelected);
  const pageSize = HORIZONTAL_PAGE_SIZE;
  const filler = children.length < pageSize;
  if (filler && children.length < pageSize && children.length % 2 === 0) {
    // "filler" item to make the children array an odd number of elements
    children = [...children, { ...fillerMock }];
  }

  const visibleChildren = children.slice(0, pageSize);
  const highlightIndex = Math.floor(visibleChildren.length / 2);
  const activeChild = highlightIndex;

  return {
    children,
    visibleChildren,
    highlightIndex,
    activeChild,
  };
};

export const getGridPageSize = (nodePreviewSelected, gridType) => nodePreviewSelected.isRoot
    ? GRID_PAGE_SIZE[gridType]?.root
    : GRID_PAGE_SIZE[gridType]?.default;

export const configureGridMenu = (nodePreviewSelected, gridType = MENU_STYLE.grid) => {
  const children = getNodeChildrenActive(nodePreviewSelected);
  const pageSize = getGridPageSize(nodePreviewSelected, gridType);
  const numPages = Math.ceil(children.length / pageSize);

  return {
    children,
    numPages,
    pageSize,
  };
};
